import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Pencil, Trash2, Plus, Copy, ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import Header from './Header';
import { BACKEND_URL } from './backendConfig';
import useEscapeClose from './useEscapeClose';
import { useTranslation, Trans } from 'react-i18next';
import TopScrollbar from './TopScrollbar';
import ModuleHelp from './ModuleHelp';
import './RoleManagement.css';

/* THE MATRIX READS LIKE THE SIDEBAR (Max 30/10: "cái role với cái side bar chưa khớp á"). The registry's groups and names
   (server.js FUNCTION_REGISTRY) are the code's — "Sale Operation", "Sales Report Dashboard", System Project under
   Documents — while people look for what the menu says. So a top-level function sits in the sidebar group that shows it,
   in the sidebar's order, under the sidebar's name (the registry name stays beside it, small, and is searchable); its
   sub-permissions follow it as before. The account-menu screens (users, roles, log…) have a group of their own; a function
   placed nowhere keeps its registry group, at the end. When a module moves in the sidebar (Header.jsx), move it here.
   Moved 30/10 with the sidebar (Jay, task #99): Other tools is gone, System Project is HEVN Admin's. Product
   Line has its own menu but no key of its own — it rides on sp_hub, listed under Production with Style Hub. */
/* THE GUIDE (Max 01/11: every module has one). The matrix is wide: the guide points at its role headers and a first cell. */
const TOUR = [
  { selector: '.rm-create', title: 'tourRoles.createT', body: 'tourRoles.createB' },
  { selector: '.rm-matrix-tools', title: 'tourRoles.toolsT', body: 'tourRoles.toolsB' },
  { selector: '.rm-matrix thead .rm-role-col', title: 'tourRoles.roleT', body: 'tourRoles.roleB' },
  { selector: '.rm-matrix .rm-cell', title: 'tourRoles.cellT', body: 'tourRoles.cellB' },
  { selector: '.rm-note', title: 'tourRoles.noteT', body: 'tourRoles.noteB' },
];

const SIDEBAR_GROUPS = [
  ['ceo', 'nav.ceoFolder'], ['report', 'nav.salesReport'], ['development', 'nav.development'],
  ['salesOp', 'nav.salesOperation'], ['costing', 'nav.costing'], ['production', 'nav.production'], ['aiHub', 'xAiHub.nav'],
  ['documents', 'nav.documents'], ['gtm', 'nav.gtm'], ['finance', 'nav.finance'], ['salesPlanning', 'nav.salesPlanning'],
  ['hevnAdmin', 'nav.hevnAdmin'], ['account', 'xRoles.accountMenu'],
];
const SIDEBAR_PLACE = [
  ['ceo_office', 'ceo', 'nav.ceoOffice'],
  ['product_sales_dashboard', 'report', 'nav.productSalesDashboard'], ['sales_report_dashboard', 'report', 'nav.salesReportDashboard'],
  ['demand_forecast_dashboard', 'report', 'nav.demandForecastDashboard'], ['data_explorer', 'report', 'nav.dataExplorer'],
  ['inventory_dashboard', 'report', null], ['marketing_dashboard', 'report', null],
  ['inventory_report', 'report', 'nav.inventoryReport'], ['sales_cw_load', 'report', 'nav.salesDataLoad'],
  ['sample_tracker', 'development', 'nav.sampleTracker'], ['partner_samples', 'development', 'nav.partnerSamples'], ['workload_snapshot', 'development', null],
  ['project_tracker', 'salesOp', 'nav.projectTracker'], ['ticket_station', 'salesOp', 'nav.ticketStation'],
  ['courier_request', 'salesOp', null], ['courier_approval', 'salesOp', null], ['auto_jobs', 'salesOp', 'nav.autoJobs'],
  ['opa_sct', 'costing', 'nav.opaSct'], ['collab_pricing', 'costing', null],
  ['nfc_portal', 'production', 'nav.nfcTokenPortal'], ['sp_hub', 'production', 'xShell.styleHub'], ['capacity', 'production', 'xShell.capacity'],
  ['sap_ship_doc', 'production', 'xShell.sapShipDoc'], ['noos_dashboard', 'production', 'xNoos.nav'],
  ['pole_inventory', 'production', 'nav.poleInventory'], ['eusu_stock', 'production', 'nav.eusuStock'],
  ['ai_hub', 'aiHub', 'xAiHub.nav'], ['ai_assistant', 'aiHub', 'ask.launch'], ['portal_mcp', 'aiHub', null], ['chat_digest', 'aiHub', null],
  ['document_library', 'documents', 'nav.documentLibrary'],
  ['gtm_programme', 'gtm', null], ['gtm_approval', 'gtm', null],
  ['cash_report', 'finance', 'nav.cashReport'],
  ['sales_planning', 'salesPlanning', null], ['sales_planning_manager', 'salesPlanning', null], ['sales_planning_factory', 'salesPlanning', null],
  ['task_board', 'hevnAdmin', 'xTask.nav'], ['driver_log', 'hevnAdmin', null], ['driver_log_admin', 'hevnAdmin', null],
  ['user_management', 'account', null], ['role_management', 'account', null], ['audit_log', 'account', null],
  ['usage_analytics', 'account', null], ['quote_management', 'account', null], ['master_data', 'account', null],
];
const PLACE = new Map(SIDEBAR_PLACE.map(([k, g, l], i) => [k, { group: g, label: l, order: i }]));

const API = `${BACKEND_URL}/api/admin`;

function RoleManagement() {
  const { t } = useTranslation();
  const [registry, setRegistry] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [editRole, setEditRole] = useState(null); // { roleKey, roleName, description, isActive }
  const [dupFor, setDupFor] = useState(null); // { sourceKey, sourceName, name }
  const [dupError, setDupError] = useState('');

  // Esc closes the top-most dialog, and never while a save is in flight.
  useEscapeClose(() => setEditRole(null), !!editRole && !busy);
  useEscapeClose(() => { setDupFor(null); setDupError(''); }, !!dupFor && !busy);
  const [collapsed, setCollapsed] = useState(() => new Set());     // collapsed module groups
  const [collapsedFns, setCollapsedFns] = useState(() => new Set()); // collapsed parent functions (hide children)
  const [filterKeys, setFilterKeys] = useState([]); // selected role keys to show (empty = all)
  /* FIND A FUNCTION BY NAME. Thirty-one of them under six collapsible headings, and the name in the
     matrix is not always the name on the menu — Max went looking for Daily Output and for the
     Document Library and found neither, because one was called "Capacity — vendor output entry" and
     the other sat under Development (2026-09-13). Both were fixed at the source; this is so the next
     one does not need fixing to be findable. */
  const [fnQuery, setFnQuery] = useState('');
  const rolePickerRef = useRef(null);
  const matrixRef = useRef(null);   // the matrix's scrolling box (TopScrollbar mirrors it)
  const toggleFilterKey = (key) => setFilterKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const toggleGroup = (group) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(group)) next.delete(group); else next.add(group);
    return next;
  });
  const toggleFn = (key) => setCollapsedFns((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const flash = (msg, ok = true) => { if (ok) { setMessage(msg); setError(''); } else { setError(msg); setMessage(''); } };
  useEffect(() => {
    if (!message && !error) return undefined;
    const t = setTimeout(() => { setMessage(''); setError(''); }, 6000);
    return () => clearTimeout(t);
  }, [message, error]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/roles`);
      setRegistry(res.data.registry || []);
      setRoles(res.data.roles || []);
    } catch (err) {
      setError(err.response?.data?.error || t('xRoles.loadFailed'));
    } finally {
      setLoading(false);
    }
  // `t` is left out on purpose: a language switch must not fetch the roles again.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  // Close the role-filter dropdown when clicking outside it.
  useEffect(() => {
    const onDown = (e) => {
      const el = rolePickerRef.current;
      if (el?.open && !el.contains(e.target)) el.removeAttribute('open');
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Functions grouped by module, each parent immediately followed by its children (depth for indent).
  const groups = useMemo(() => {
    /* A search keeps the PARENT of anything it matches, or a matched child would render with nothing
       to be a child of — and the row that carries the tick for "everything under here". */
    const term = fnQuery.trim().toLowerCase();
    // the sidebar's place and name of each function (a child goes where its parent goes, under its own name)
    const named = registry.map((f) => {
      const top = PLACE.get(f.parent || f.key);
      const own = PLACE.get(f.key);
      const label = own && own.label ? t(own.label) : '';
      return { ...f, codeName: f.name, name: label && label !== f.name ? label : f.name, alias: label && label !== f.name ? f.name : '',
        groupKey: top ? top.group : `reg:${f.group}`, order: top ? top.order : 9999 };
    });
    const hit = (f) => !term || f.name.toLowerCase().includes(term) || f.codeName.toLowerCase().includes(term) || f.key.toLowerCase().includes(term);
    const keep = new Set(named.filter(hit).map((f) => f.key));
    named.filter((f) => keep.has(f.key) && f.parent).forEach((f) => keep.add(f.parent));
    const shown = (term ? named.filter((f) => keep.has(f.key)) : named).sort((a, b) => a.order - b.order);
    const groupOrder = new Map(SIDEBAR_GROUPS.map(([g], i) => [g, i]));
    const byGroup = new Map();
    for (const fn of [...shown].sort((a, b) => (groupOrder.get(a.groupKey) ?? 99) - (groupOrder.get(b.groupKey) ?? 99))) {
      if (!byGroup.has(fn.groupKey)) byGroup.set(fn.groupKey, []);
      byGroup.get(fn.groupKey).push(fn);
    }
    const groupName = (g) => (g.startsWith('reg:') ? g.slice(4) : t(SIDEBAR_GROUPS.find(([k]) => k === g)?.[1] || g));
    return [...byGroup.entries()].map(([gk, fns]) => {
      const group = groupName(gk);
      const parents = fns.filter((f) => !f.parent);
      const rows = [];
      for (const p of parents) {
        rows.push({ ...p, depth: 0 });
        fns.filter((c) => c.parent === p.key).forEach((c) => rows.push({ ...c, depth: 1 }));
      }
      // any child whose parent is in another group (none today) — append defensively
      fns.filter((f) => f.parent && !parents.some((p) => p.key === f.parent)).forEach((c) => rows.push({ ...c, depth: 1 }));
      return { group, rows };
    });
  }, [registry, fnQuery, t]);

  const parentKeys = useMemo(() => new Set(registry.filter((f) => f.parent).map((f) => f.parent)), [registry]);
  const visibleRoles = useMemo(() => (
    filterKeys.length ? roles.filter((r) => filterKeys.includes(r.roleKey)) : roles
  ), [roles, filterKeys]);
  const collapseAll = () => setCollapsed(new Set(groups.map((g) => g.group)));
  const expandAll = () => { setCollapsed(new Set()); setCollapsedFns(new Set()); };

  // level: 'none' | 'view' | 'edit'
  const setLevel = async (roleKey, functionKey, level) => {
    setBusy(true);
    setRoles((prev) => prev.map((r) => {
      if (r.roleKey !== roleKey) return r;
      const next = { ...r.functions };
      if (level === 'none') delete next[functionKey]; else next[functionKey] = level;
      return { ...r, functions: next };
    }));
    try {
      await axios.post(`${API}/roles/${encodeURIComponent(roleKey)}/function`, { functionKey, level });
    } catch (err) {
      flash(err.response?.data?.error || t('xRoles.permFailed'), false);
      await load();
    } finally { setBusy(false); }
  };

  const createRole = async () => {
    const roleName = newRoleName.trim();
    if (!roleName) { flash(t('xRoles.enterName'), false); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/roles`, { roleName, description: newRoleDesc.trim() });
      flash(t('xRoles.created', { name: roleName }));
      setNewRoleName(''); setNewRoleDesc('');
      await load();
    } catch (err) { flash(err.response?.data?.error || t('xRoles.createFailed'), false); }
    finally { setBusy(false); }
  };

  const duplicateRole = async () => {
    if (!dupFor) return;
    const roleName = dupFor.name.trim();
    if (!roleName) { setDupError(t('xRoles.enterDupName')); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/roles/${encodeURIComponent(dupFor.sourceKey)}/duplicate`, { roleName });
      flash(t('xRoles.duplicated', { name: roleName, source: dupFor.sourceName }));
      setDupFor(null); setDupError('');
      await load();
    } catch (err) { setDupError(err.response?.data?.error || t('xRoles.dupFailed')); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editRole) return;
    const roleName = editRole.roleName.trim();
    if (!roleName) { flash(t('xRoles.nameRequired'), false); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/roles/${encodeURIComponent(editRole.roleKey)}`, {
        roleName, description: editRole.description, isActive: editRole.isActive,
      });
      flash(t('xRoles.updated'));
      setEditRole(null);
      await load();
    } catch (err) { flash(err.response?.data?.error || t('xRoles.updateFailed'), false); }
    finally { setBusy(false); }
  };

  const deleteRole = async (role) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('xRoles.confirmDelete', { name: role.roleName }))) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/roles/${encodeURIComponent(role.roleKey)}`);
      flash(t('xRoles.deleted', { name: role.roleName }));
      await load();
    } catch (err) { flash(err.response?.data?.error || t('xRoles.deleteFailed'), false); }
    finally { setBusy(false); }
  };

  return (
    <div className="rm-page">
      <Header />
      <main className="rm-shell">
        <div className="rm-app">
          <div className="rm-head mh-titlerow">
            <div>
              <h1>{t('xRoles.title')}</h1>
              <p>{t('xRoles.intro')}</p>
            </div>
            <ModuleHelp steps={TOUR} tourKey="roles" ready={!loading} />
          </div>

          {(message || error) && <div className={`rm-alert ${error ? 'error' : 'success'}`}>{error || message}</div>}

          {loading ? <div className="rm-card">{t('xRoles.loading')}</div> : (
            <>
              <div className="rm-card">
                <h2>{t('xRoles.createTitle')}</h2>
                <div className="rm-create">
                  <input placeholder={t('xRoles.namePh')} value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
                  <input placeholder={t('xRoles.descPh')} value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} />
                  <button type="button" className="rm-btn primary" disabled={busy || !newRoleName.trim() || roles.some((r) => r.roleName.trim().toLowerCase() === newRoleName.trim().toLowerCase())} onClick={createRole}><Plus size={15} /> {t('xRoles.create')}</button>
                </div>
                {newRoleName.trim() && roles.some((r) => r.roleName.trim().toLowerCase() === newRoleName.trim().toLowerCase()) && (
                  <div className="rm-field-error">{t('xRoles.exists', { name: newRoleName.trim() })}</div>
                )}
              </div>

              <div className="rm-card">
                <div className="rm-matrix-bar">
                  <h2>{t('xRoles.matrix')}</h2>
                  <div className="rm-matrix-tools">
                    <details className="rm-role-picker" ref={rolePickerRef}>
                      <summary>{filterKeys.length ? t(filterKeys.length > 1 ? 'xRoles.rolesShownMany' : 'xRoles.rolesShownOne', { n: filterKeys.length }) : t('xRoles.allRoles')}<ChevronDown size={14} /></summary>
                      <div className="rm-role-picker-panel">
                        <label className="rm-role-picker-row"><input type="checkbox" checked={!filterKeys.length} onChange={() => setFilterKeys([])} /> {t('xRoles.allRoles')}</label>
                        <div className="rm-role-picker-list">
                          {roles.map((r) => (
                            <label className="rm-role-picker-row" key={r.roleKey}>
                              <input type="checkbox" checked={filterKeys.includes(r.roleKey)} onChange={() => toggleFilterKey(r.roleKey)} /> {r.roleName}
                            </label>
                          ))}
                        </div>
                      </div>
                    </details>
                    <span className="rm-find">
                      <Search size={13} />
                      <input
                        value={fnQuery}
                        onChange={(e) => { setFnQuery(e.target.value); if (e.target.value.trim()) setCollapsed(new Set()); }}
                        placeholder={t('xRoles.findPh')}
                      />
                      {!!fnQuery && (
                        <button type="button" onClick={() => setFnQuery('')}><X size={12} /></button>
                      )}
                    </span>
                    <button type="button" className="rm-btn ghost sm" onClick={collapseAll}>{t('xRoles.collapseAll')}</button>
                    <button type="button" className="rm-btn ghost sm" onClick={expandAll}>{t('xRoles.expandAll')}</button>
                  </div>
                </div>
                {/* Jay 30/10: the matrix scrolls inside a box the height of the screen — its vertical bar on the right, the
                    horizontal one at the box's foot AND on top (TopScrollbar); the role row and the Function column stay put */}
                <TopScrollbar target={matrixRef} className="rm-top-scroll" />
                <div className="rm-matrix-scroll" ref={matrixRef} data-rm-scroll>
                  <table className="rm-matrix">
                    <thead>
                      <tr>
                        <th className="rm-fn-col">{t('xRoles.colFunction')}</th>
                        {visibleRoles.map((r) => (
                          <th key={r.roleKey} className={`rm-role-col${r.isActive ? '' : ' inactive'}`}>
                            <div className="rm-role-name">{r.roleName}{!r.isActive && <span className="rm-tag">{t('xRoles.inactive')}</span>}</div>
                            <div className="rm-role-meta">{t(r.userCount === 1 ? 'xRoles.userOne' : 'xRoles.userMany', { n: r.userCount })}</div>
                            <div className="rm-role-actions">
                              <button type="button" title={t('xRoles.editRole')} onClick={() => setEditRole({ roleKey: r.roleKey, roleName: r.roleName, description: r.description || '', isActive: r.isActive })}><Pencil size={13} /></button>
                              <button type="button" title={t('xRoles.dupRole')} onClick={() => setDupFor({ sourceKey: r.roleKey, sourceName: r.roleName, name: `${r.roleName} copy` })}><Copy size={13} /></button>
                              {!r.isSystem && <button type="button" title={t('xRoles.delRole')} className="danger" onClick={() => deleteRole(r)}><Trash2 size={13} /></button>}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {groups.map(({ group, rows }) => (
                        <React.Fragment key={group}>
                          <tr className="rm-group-row" onClick={() => toggleGroup(group)}>
                            <td colSpan={visibleRoles.length + 1}>
                              <span className="rm-group-toggle">
                                {collapsed.has(group) ? <ChevronRight size={15} /> : <ChevronDown size={15} />}
                                {group}
                              </span>
                            </td>
                          </tr>
                          {!collapsed.has(group) && rows
                            .filter((fn) => !(fn.depth && collapsedFns.has(fn.parent)))
                            .map((fn) => {
                            const isParent = parentKeys.has(fn.key);
                            const fnCollapsed = collapsedFns.has(fn.key);
                            return (
                            <tr key={fn.key} className={fn.depth ? 'rm-child-row' : ''}>
                              <td className={`rm-fn-col${fn.depth ? ' child' : ''}`}>
                                {fn.depth ? '↳ ' : ''}
                                {isParent
                                  ? <button type="button" className="rm-fn-toggle" onClick={() => toggleFn(fn.key)}>
                                      {fnCollapsed ? <ChevronRight size={13} /> : <ChevronDown size={13} />}{fn.name}
                                      {fn.alias && <span className="rm-fn-alias">{fn.alias}</span>}
                                    </button>
                                  : <>{fn.name}{fn.alias && <span className="rm-fn-alias">{fn.alias}</span>}</>}
                              </td>
                              {visibleRoles.map((r) => {
                                const level = r.functions[fn.key]; // 'view' | 'edit' | undefined
                                const viewOn = level === 'view' || level === 'edit';
                                const editOn = level === 'edit';
                                return (
                                  <td key={r.roleKey} className="rm-cell">
                                    <div className="rm-ve">
                                      <label title={t('xRoles.canView')}>
                                        <input type="checkbox" disabled={busy} checked={viewOn}
                                          onChange={(e) => setLevel(r.roleKey, fn.key, e.target.checked ? 'view' : 'none')} />
                                        <span>{t('xRoles.view')}</span>
                                      </label>
                                      {fn.hasEdit && (
                                        <label title={t('xRoles.canEdit')}>
                                          <input type="checkbox" disabled={busy} checked={editOn}
                                            onChange={(e) => setLevel(r.roleKey, fn.key, e.target.checked ? 'edit' : 'view')} />
                                          <span>{t('xRoles.edit')}</span>
                                        </label>
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                      {!roles.length && <tr><td colSpan={1} className="rm-empty">{t('xRoles.noRoles')}</td></tr>}
                    </tbody>
                  </table>
                </div>
                <p className="rm-note">{t('xRoles.note')}</p>
              </div>
            </>
          )}
        </div>
      </main>

      {editRole && (
        <div className="rm-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setEditRole(null); }}>
          <div className="rm-modal">
            <div className="rm-modal-head"><h3>{t('xRoles.editRole')}</h3><button type="button" onClick={() => setEditRole(null)}>&times;</button></div>
            <div className="rm-modal-body">
              <label>{t('xRoles.roleName')}<input value={editRole.roleName} onChange={(e) => setEditRole({ ...editRole, roleName: e.target.value })} /></label>
              <label>{t('xRoles.description')}<textarea rows={3} value={editRole.description} onChange={(e) => setEditRole({ ...editRole, description: e.target.value })} /></label>
              <label className="rm-check"><input type="checkbox" checked={editRole.isActive} onChange={(e) => setEditRole({ ...editRole, isActive: e.target.checked })} /> {t('xRoles.activeLabel')}</label>
            </div>
            <div className="rm-modal-foot">
              <button type="button" className="rm-btn primary" disabled={busy} onClick={saveEdit}>{t('xRoles.save')}</button>
              <button type="button" className="rm-btn ghost" onClick={() => setEditRole(null)}>{t('xRoles.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {dupFor && (
        <div className="rm-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) { setDupFor(null); setDupError(''); } }}>
          <div className="rm-modal">
            <div className="rm-modal-head"><h3>{t('xRoles.dupRole')}</h3><button type="button" onClick={() => { setDupFor(null); setDupError(''); }}>&times;</button></div>
            <div className="rm-modal-body">
              <p className="rm-dup-hint"><Trans t={t} i18nKey="xRoles.dupHint" values={{ source: dupFor.sourceName }} components={{ b: <b /> }} /></p>
              <label>{t('xRoles.newRoleName')}<input autoFocus value={dupFor.name} onChange={(e) => { setDupFor({ ...dupFor, name: e.target.value }); if (dupError) setDupError(''); }} /></label>
              {dupFor.name.trim() && roles.some((r) => r.roleName.trim().toLowerCase() === dupFor.name.trim().toLowerCase()) && (
                <div className="rm-field-error">{t('xRoles.exists', { name: dupFor.name.trim() })}</div>
              )}
              {dupError && <div className="rm-field-error">{dupError}</div>}
            </div>
            <div className="rm-modal-foot">
              <button type="button" className="rm-btn primary" disabled={busy || !dupFor.name.trim() || roles.some((r) => r.roleName.trim().toLowerCase() === dupFor.name.trim().toLowerCase())} onClick={duplicateRole}>{t('xRoles.duplicate')}</button>
              <button type="button" className="rm-btn ghost" onClick={() => { setDupFor(null); setDupError(''); }}>{t('xRoles.cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleManagement;
